import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "../../contexts/AppContext";
import { executionService } from "../../services/executionService";
import { crafExecutionService } from "../../services/crafExecutionService";
import { pdfService } from "../../services/pdfService";
import { buildFolderPath } from "../../services/contractService";
import { PdfSigningViewer } from "./PdfSigningViewer";

export const ApproverSigningPage = () => {
  const navigate = useNavigate();
  const { service, currentUser } = useApp();
  const { executionId } = useParams();

  const [execution, setExecution] = useState(null);
  const [approver, setApprover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [crafDocLink, setCrafDocLink] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [preAssignedSignature, setPreAssignedSignature] = useState(null);
  const [showSignatureUploadModal, setShowSignatureUploadModal] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadExecutionData();
    }
  }, [executionId, currentUser]);

  useEffect(() => {
    if (approver?.ApproverEmail) {
      loadPreAssignedSignature();
    }
  }, [approver?.ApproverEmail]);

  const loadExecutionData = async () => {
    setLoading(true);
    try {
      const exec = await executionService.getExecution(service, executionId);
      if (!exec) {
        setError("Execution not found");
        setLoading(false);
        return;
      }

      setExecution(exec);

      // Get all approvers for this execution
      const allApprovers = await executionService.getExecutionApprovers(service, executionId);

      if (!allApprovers || allApprovers.length === 0) {
        setError("No approvers found for this execution");
        setLoading(false);
        return;
      }

      // Check if current user is in the approver list
      const currentUserEmail = (currentUser?.mail || currentUser?.userPrincipalName || "").toLowerCase();
      const currentUserApprover = allApprovers.find(a =>
        a.ApproverEmail && currentUserEmail &&
        a.ApproverEmail.toLowerCase() === currentUserEmail
      );

      if (!currentUserApprover) {
        setError("You are not an approver for this contract");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // Check approver status
      if (currentUserApprover.Status === "Approved") {
        setError("You have already signed this contract. No further action is needed.");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (currentUserApprover.Status === "Rejected") {
        setError("You have rejected this contract. Access is no longer available.");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (currentUserApprover.Status === "Skipped") {
        setError("This contract approval was skipped for you. Access is no longer available.");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (currentUserApprover.Status === "Waiting") {
        setError("It is not your turn to approve yet. Please wait for your turn.");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      if (currentUserApprover.Status !== "Pending") {
        setError("You do not have permission to access this contract at this time");
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      // User is authorized - set their approver data
      setApprover(currentUserApprover);

      // Load PDF from SharePoint
      try {
        console.log('[ApproverSigningPage] Loading PDF for execution:', executionId);

        // Get the contract to find folder/drive info
        const contract = await service.getListItem("Contracts", exec.ContractID);
        const contractData = contract.fields;

        // Find ContractDocuments drive
        const drives = await service.getDrives();
        const docsDrive = drives?.value?.find(d => d.name === "ContractDocuments");

        if (!docsDrive) {
          console.warn('[ApproverSigningPage] ContractDocuments drive not found');
          return;
        }

        const driveId = docsDrive.id;
        console.log('[ApproverSigningPage] Using drive:', driveId);

        // Build folder path using same method as upload
        const folderPath = buildFolderPath(
          contractData.Entity,
          contractData.Department,
          contractData.Year,
          contractData.Month,
          contractData.RefNo
        );

        const pdfFileName = pdfService.getExecutedPdfName(exec.ContractTitle);
        const fullPath = `${folderPath}/${pdfFileName}.pdf`;

        console.log('[ApproverSigningPage] Looking for PDF at:', fullPath);

        // Get folder contents to find the PDF file
        try {
          const folderContents = await service.getFolderContents(driveId, folderPath);

          // CRAF's executed PDF, if the CRAF has been sent for execution —
          // linked here so approvers signing the main contract can see it too.
          const crafPdfName = crafExecutionService.getCrafPdfName(exec.ContractRefNo);
          const crafPdfFile = folderContents?.value?.find(item =>
            !item.folder && item.name && item.name.includes(crafPdfName)
          );
          if (crafPdfFile?.webUrl) {
            setCrafDocLink(crafPdfFile.webUrl);
          }

          const pdfFile = folderContents?.value?.find(item =>
            item.name && item.name.includes(pdfFileName) && !item.folder
          );

          if (pdfFile) {
            console.log('[ApproverSigningPage] ✅ Found PDF:', pdfFile.name, 'ID:', pdfFile.id);

            // Download the PDF file using Graph API (includes auth)
            console.log('[ApproverSigningPage] Downloading PDF from path:', fullPath);

            // Use downloadFile to get the authenticated content
            try {
              const pdfArrayBuffer = await service.downloadFile(driveId, fullPath);
              console.log('[ApproverSigningPage] Downloaded PDF size:', pdfArrayBuffer?.byteLength, 'bytes');

              if (!pdfArrayBuffer || pdfArrayBuffer.byteLength === 0) {
                console.error('[ApproverSigningPage] PDF data is empty!');
                return;
              }

              // Convert ArrayBuffer to Blob and then to data URL
              const pdfBlob = new Blob([pdfArrayBuffer], { type: 'application/pdf' });
              const pdfDataUrlValue = URL.createObjectURL(pdfBlob);

              console.log('[ApproverSigningPage] Created blob URL:', pdfDataUrlValue.substring(0, 50));
              setPdfDataUrl(pdfDataUrlValue);
              console.log('[ApproverSigningPage] ✅ PDF state updated');
            } catch (downloadError) {
              console.error('[ApproverSigningPage] Failed to download PDF:', downloadError.message);
              console.error('[ApproverSigningPage] Full error:', downloadError);
            }
          } else {
            console.warn('[ApproverSigningPage] PDF file not found in folder');
            console.log('[ApproverSigningPage] Files in folder:', folderContents?.value?.map(f => f.name));
          }
        } catch (folderError) {
          console.warn('[ApproverSigningPage] Could not access folder:', folderError);
        }
      } catch (docError) {
        console.error('[ApproverSigningPage] Error loading contract/PDF:', docError);
      }
    } catch (e) {
      console.error("Error loading execution:", e);
      setError("Failed to load execution details");
    } finally {
      setLoading(false);
    }
  };

  const loadPreAssignedSignature = async () => {
    try {
      // Get the signature assigned to this approver's email
      const signatures = await service.getListItems(
        "ApproverSignatures",
        `fields/ApproverEmail eq '${approver.ApproverEmail}'`
      );

      if (signatures?.value?.length > 0) {
        // Duplicates may exist from before the save-signature fix — use the
        // most recently saved one rather than whatever order the API returns.
        const latest = signatures.value
          .sort((a, b) => new Date(b.fields?.CreatedAt || 0) - new Date(a.fields?.CreatedAt || 0))[0];
        const signature = latest.fields;
        setPreAssignedSignature(signature.SignatureData);
        console.log('[ApproverSigningPage] ✓ Loaded pre-assigned signature for', approver.ApproverEmail);
      } else {
        console.log('[ApproverSigningPage] ℹ️ No pre-assigned signature for', approver.ApproverEmail);
        setPreAssignedSignature(null);
        // Show upload modal since no signature exists
        setShowSignatureUploadModal(true);
      }
    } catch (e) {
      console.error('[ApproverSigningPage] Error loading pre-assigned signature:', e);
    }
  };

  const handleSaveUserSignature = async (signatureBase64) => {
    if (!signatureBase64 || !approver?.ApproverEmail) return;

    setUploadingSignature(true);
    try {
      // One signature per email, ever — update if one already exists
      // (e.g. a race with another tab/contract) instead of duplicating.
      const existing = await service.getListItems(
        "ApproverSignatures",
        `fields/ApproverEmail eq '${approver.ApproverEmail}'`
      );
      // Duplicates may already exist from before this fix — pick the most
      // recent one rather than whatever order the API happens to return.
      const existingRecord = (existing?.value || [])
        .sort((a, b) => new Date(b.fields?.CreatedAt || 0) - new Date(a.fields?.CreatedAt || 0))[0];
      const fields = {
        Title: `${approver.ApproverEmail} - ${new Date().toLocaleDateString()}`,
        ApproverEmail: approver.ApproverEmail,
        SignatureData: signatureBase64,
        CreatedAt: new Date().toISOString()
      };

      let signature;
      if (existingRecord) {
        await service.updateListItem("ApproverSignatures", existingRecord.id, fields);
        signature = { id: existingRecord.id, ...fields };
      } else {
        signature = await service.createListItem("ApproverSignatures", fields);
      }

      console.log('[ApproverSigningPage] ✅ User signature saved:', signature.id);

      // Update local state with the saved signature
      setPreAssignedSignature(signatureBase64);
      setShowSignatureUploadModal(false);
    } catch (error) {
      console.error('[ApproverSigningPage] Error saving signature:', error);
      alert("Error saving signature: " + error.message);
    } finally {
      setUploadingSignature(false);
    }
  };

  const handleSignatureCaptured = async (sigData) => {
    console.log('[ApproverSigningPage] 🖊️ Signature captured:', {
      hasData: !!sigData.signatureData,
      page: sigData.signaturePage,
      textBoxCount: sigData.textBoxes?.length || 0,
      position: sigData.signaturePosition
    });

    // Directly approve without needing positioner modal
    await handleApproveWithPositions(sigData);
  };

  const handleApproveWithPositions = async (sigData) => {
    if (!sigData?.signatureData) {
      alert("Please position your signature first");
      return;
    }

    setSubmitting(true);
    try {
      // Save signature record (only signature, not text - for reusability)
      const signatureRecord = await executionService.saveApproverSignature(service, {
        approverId: approver.id,
        approverEmail: approver.ApproverEmail,
        signatureBase64: sigData.signatureData,
        signatureFileName: `${approver.ApproverEmail}_${new Date().getTime()}.png`,
      });

      // Get contract details for drive/folder info
      const contractData = await service.getListItem("Contracts", execution.ContractID);
      const contract = contractData.fields;
      const driveId = contract.DriveId;
      const contractFolder = buildFolderPath(
        contract.Entity,
        contract.Department,
        contract.Year,
        contract.Month,
        contract.RefNo
      );

      // Approve with signature data and text boxes
      await executionService.approveExecution(service, {
        approverId: approver.id,
        executionId: executionId,
        approverEmail: approver.ApproverEmail,
        signatureId: signatureRecord.id,
        comments: "",
        signatureData: sigData.signatureData,
        signaturePage: sigData.signaturePage,
        signatureX: sigData.signaturePosition?.x,
        signatureY: sigData.signaturePosition?.y,
        signatureWidth: sigData.signaturePosition?.width,
        signatureHeight: sigData.signaturePosition?.height,
        approverName: approver.ApproverName,
        textBoxes: sigData.textBoxes || [],  // Include text boxes created in viewer
        driveId: driveId,
        contractFolder: contractFolder
      });

      // Check if execution is complete
      const updatedExecution = await executionService.getExecution(service, executionId);
      const approvers = await executionService.getExecutionApprovers(service, executionId);
      const allApproved = approvers.every((a) => a.Status === "Approved");

      if (allApproved && updatedExecution.Status === "Completed") {
        alert("All approvers have signed! The contract execution is now complete. Thank you!");
      } else if (!allApproved) {
        const nextApprover = approvers.find((a) => a.Status === "Waiting");
        alert("Your signature has been recorded. Thank you!");
      } else {
        alert("Your signature has been recorded. Thank you!");
      }

      navigate("/");
    } catch (error) {
      console.error("Error approving:", error);
      alert("Error recording approval: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }

    setRejecting(true);
    try {
      await executionService.rejectExecution(service, {
        approverId: approver.id,
        reason: rejectReason,
      });

      alert("Contract has been rejected. The contract owner has been notified.");
      navigate("/");
    } catch (error) {
      console.error("Error rejecting:", error);
      alert("Error rejecting: " + error.message);
    } finally {
      setRejecting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-700 border-t-yellow-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading contract...</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-5">
        <div className="bg-slate-800 border border-red-700 rounded-xl p-8 max-w-md text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-red-600 font-serif text-2xl mb-3">Error</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm font-medium"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-950 flex flex-col">
      {/* View CRAF Document — only shown once a CRAF has been generated/sent for
          this contract. Opens in a new tab, does not disrupt the signing flow. */}
      {crafDocLink && (
        <a
          href={crafDocLink}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-3 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-1.5 px-3.5 py-1.5 bg-slate-800/95 hover:bg-slate-700 border border-slate-600 rounded-full text-xs font-medium text-yellow-500 shadow-lg backdrop-blur transition-colors"
        >
          📋 View CRAF Document ↗
        </a>
      )}

      {/* Fullscreen PDF Viewer */}
      {pdfDataUrl ? (
        <PdfSigningViewer
          pdfDataUrl={pdfDataUrl}
          onSignatureCaptured={handleSignatureCaptured}
          contractTitle={execution?.ContractTitle}
          contractRef={execution?.ContractRefNo}
          approverEmail={approver?.ApproverEmail}
          approverName={approver?.ApproverName}
          approvalOrder={approver?.ApprovalOrder}
          totalApprovers={execution?.TotalApprovers}
          preAssignedSignature={preAssignedSignature}
          isSubmitting={submitting}
        />
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-slate-400 text-center">
            <div className="text-6xl mb-4">📄</div>
            <p className="text-xl">Loading PDF...</p>
          </div>
        </div>
      )}

      {/* Signature Upload/Canvas Modal */}
      {showSignatureUploadModal && <SignatureUploadModal
        approverEmail={approver?.ApproverEmail}
        uploading={uploadingSignature}
        onSignatureSaved={handleSaveUserSignature}
        onCancel={() => setShowSignatureUploadModal(false)}
      />}

      {/* Reject Reason Modal */}
        {showRejectReason && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-5">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-slate-100 mb-4">Reject Contract</h3>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                rows={5}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 text-sm mb-4"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRejectReason(false);
                    setRejectReason("");
                  }}
                  className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={rejecting || !rejectReason.trim()}
                  className={`flex-1 px-4 py-2 text-sm rounded-lg font-medium ${
                    rejecting || !rejectReason.trim()
                      ? "bg-red-700/50 text-white cursor-not-allowed opacity-50"
                      : "bg-red-700 hover:bg-red-800 text-white cursor-pointer"
                  }`}
                >
                  {rejecting ? "Processing..." : "Confirm Rejection"}
                </button>
              </div>
            </div>
          </div>
        )}

    </div>
  );
};

// Signature Upload Modal Component
const SignatureUploadModal = ({ approverEmail, uploading, onSignatureSaved, onCancel }) => {
  const [uploadTab, setUploadTab] = React.useState("upload"); // "upload" or "canvas"
  const [uploadedImage, setUploadedImage] = React.useState(null);
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = React.useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedImage(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;

    const ctx = canvas.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;

    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000";
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleSaveSignature = () => {
    if (uploadTab === "upload" && !uploadedImage) {
      alert("Please upload a signature image");
      return;
    }

    if (uploadTab === "canvas") {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const canvasData = canvas.toDataURL("image/png");
      if (canvasData === "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==") {
        alert("Please sign on the canvas before saving");
        return;
      }
      onSignatureSaved(canvasData);
    } else {
      onSignatureSaved(uploadedImage);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-100">Your Signature</h2>
            <p className="text-sm text-slate-400 mt-1">Upload or sign on canvas to proceed</p>
          </div>
          <button
            onClick={onCancel}
            disabled={uploading}
            className="text-slate-400 hover:text-slate-100 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {/* Info message */}
          <div className="bg-blue-900/30 border border-blue-600/50 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-300">
              📝 <strong>Only you can use this signature.</strong> Once saved, your signature will be automatically used for all future approvals and will be visible to admins.
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-slate-700">
            <button
              onClick={() => setUploadTab("upload")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                uploadTab === "upload"
                  ? "text-yellow-600 border-yellow-600"
                  : "text-slate-400 border-transparent hover:text-slate-300"
              }`}
            >
              📤 Upload Image
            </button>
            <button
              onClick={() => setUploadTab("canvas")}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                uploadTab === "canvas"
                  ? "text-yellow-600 border-yellow-600"
                  : "text-slate-400 border-transparent hover:text-slate-300"
              }`}
            >
              ✏️ Sign on Canvas
            </button>
          </div>

          {/* Upload Tab */}
          {uploadTab === "upload" && (
            <div>
              {uploadedImage ? (
                <div className="flex flex-col gap-4">
                  <div className="border-2 border-dashed border-slate-600 rounded-lg p-4 bg-slate-700/30">
                    <img src={uploadedImage} alt="Signature preview" className="max-h-32 mx-auto" />
                  </div>
                  <label className="block">
                    <span className="text-sm text-slate-300 mb-2 block">Change image:</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="block w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-yellow-600 file:text-slate-900"
                    />
                  </label>
                </div>
              ) : (
                <label className="block">
                  <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-yellow-600/50 transition-colors">
                    <div className="text-4xl mb-2">📸</div>
                    <p className="text-slate-300 mb-1">Click to upload your signature</p>
                    <p className="text-xs text-slate-500">PNG, JPG or GIF (recommended: transparent background)</p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          )}

          {/* Canvas Tab */}
          {uploadTab === "canvas" && (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-slate-400">Sign below. Touch or click and drag to draw your signature.</p>
              <canvas
                ref={canvasRef}
                width={500}
                height={150}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                className="border-2 border-slate-600 rounded-lg bg-white cursor-crosshair w-full touch-none"
                style={{ touchAction: "none" }}
              />
              <button
                onClick={clearCanvas}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg font-medium"
              >
                Clear
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-8">
            <button
              onClick={onCancel}
              disabled={uploading}
              className="flex-1 px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm font-medium rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSignature}
              disabled={uploading || (!uploadedImage && uploadTab === "upload")}
              className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                uploading || (!uploadedImage && uploadTab === "upload")
                  ? "bg-yellow-600/50 text-slate-900 cursor-not-allowed opacity-50"
                  : "bg-yellow-600 hover:bg-yellow-700 text-slate-900 cursor-pointer"
              }`}
            >
              {uploading ? "Saving..." : "Save & Continue"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
